import { useState } from 'react'
import { useTexte } from '../../texte'

import { istEmoji } from '../../lib/emoji'
import { EMOJIGRUPPEN } from '../../lib/emojiliste'

/**
 * Die volle Auswahl — Reiter, Raster und ein Feld für alles andere.
 *
 * **Eigenes Bauteil, weil es zwei Aufrufer gibt.** Sie stand in `Reactions`,
 * und als die schnelle Achterreihe ins Menü an der Sprechblase umzog (#8),
 * blieb sie dort zurück: das `＋` für die ganze Tastatur war an der Nachricht
 * plötzlich weg. Aus dem Betrieb: „in der Smiley-Ansicht fehlt jetzt das plus
 * für weitere Emojis." Zweimal dasselbe zu bauen wäre der sichere Weg dazu,
 * dass es auseinanderläuft.
 *
 * **Der Weg führt nicht über die Tastatur des Geräts.** Die erste Version
 * öffnete ein Feld, damit sie aufgeht — sie geht auch auf, aber auf der
 * Buchstaben-Ebene, und welche Ebene eine Tastatur zeigt, lässt sich nicht
 * setzen. Deshalb eine eigene Auswahl, die überall gleich aussieht: auf dem
 * iPhone, unter Android und am Rechner.
 */
export function Emojiwahl({
  imFluss,
  onPick,
  onZu,
}: {
  /**
   * Im Fluss statt schwebend — dort, wo ein Scrollkasten sie sonst
   * abschneidet. Wer sie einsetzt, weiß das; der Browser weiß es nicht.
   */
  imFluss?: boolean
  onPick: (emoji: string) => void
  /** Escape im Feld, und nach der Wahl. */
  onZu: () => void
}) {
  const t = useTexte()
  const [gruppe, setGruppe] = useState(0)
  const [getippt, setGetippt] = useState('')
  const [schief, setSchief] = useState(false)

  return (
    <div
      className={imFluss ? 'picker picker--alle picker--inline' : 'picker picker--alle'}
      role="menu"
      aria-label={t.chat.emojiWahl}
    >
      {/* Die Reiter tragen ein Zeichen und kein Wort: acht Wörter wären
          breiter als ein Telefon, und was dann scrollt, ist die Seite. */}
      <div className="picker__reiter" role="tablist">
        {EMOJIGRUPPEN.map((g, i) => (
          <button
            key={g.name}
            type="button"
            role="tab"
            aria-selected={i === gruppe}
            aria-label={t.technik.emojigruppen[g.name]}
            title={t.technik.emojigruppen[g.name]}
            className={
              i === gruppe ? 'picker__reiter-knopf picker__reiter-knopf--an' : 'picker__reiter-knopf'
            }
            onClick={() => setGruppe(i)}
          >
            {g.reiter}
          </button>
        ))}
      </div>

      <div className="picker__gitter">
        {(EMOJIGRUPPEN[gruppe] ?? EMOJIGRUPPEN[0]!).zeichen.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="picker__item"
            role="menuitem"
            onClick={() => {
              onPick(emoji)
              onZu()
            }}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* **Der Rest der Wahrheit.** Die Liste ist eine Auswahl und keine
          Vollständigkeit; vollständig wäre ein halbes Megabyte, das jeder
          Leser mitlädt. Wer ein Zeichen braucht, das hier fehlt, tippt es
          ein — und muss dafür in seiner Tastatur auf die Emoji-Ebene
          wechseln. Das steht so da, statt es zu verschweigen. */}
      <input
        className="picker__feld"
        type="text"
        enterKeyHint="done"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        aria-label={t.chat.emojiEigenes}
        placeholder={t.chat.emojiPlatzhalter}
        value={getippt}
        onChange={(event) => {
          const wert = event.target.value.trim()
          setGetippt(wert)
          setSchief(false)
          if (wert !== '' && istEmoji(wert)) {
            onPick(wert)
            setGetippt('')
            onZu()
          } else if (wert !== '') {
            setSchief(true)
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            // Nicht bis zum Fenster: dort schlösse Escape gleich das ganze
            // Blatt, und der Weg zurück zur Achterreihe wäre keiner.
            event.stopPropagation()
            setGetippt('')
            onZu()
          }
        }}
      />
      {schief && (
        <span className="picker__hinweis picker__hinweis--schief" role="alert">
          {t.chat.emojiNurEines}
        </span>
      )}
    </div>
  )
}
